import { useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { Toaster, toast } from "sonner";
import { 
  ChevronRight, 
  ChevronLeft, 
  Check, 
  Home, 
  FileText, 
  Banknote, 
  Calendar, 
  Phone, 
  Mail, 
  User,
  Clock,
  Loader2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_prospect-inquiry/artifacts/50oz1nnj_Logo%20BL%20def.png";
const VIDEO_BG_URL = "https://customer-assets.emergentagent.com/job_prospect-inquiry/artifacts/uybfiejm_EB9A82DC-E8D3-4FC1-B4F7-B531E2C4B24E.mp4";

const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 50 : -50,
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction) => ({
    x: direction < 0 ? 50 : -50,
    opacity: 0
  })
};

const QuestionnaireForm = () => {
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    is_land_owner: null,
    permit_status: null,
    has_consulted_bank: null,
    desired_timeline: null
  });

  const totalSteps = 6;

  const triggerConfetti = useCallback(() => {
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 999 };

    const randomInRange = (min, max) => Math.random() * (max - min) + min;

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);

      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        colors: ['#D4AF37', '#1C1C1C', '#2E8B57', '#F5F5F0']
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        colors: ['#D4AF37', '#1C1C1C', '#2E8B57', '#F5F5F0']
      });
    }, 250);
  }, []);

  const nextStep = () => {
    setDirection(1);
    setStep(prev => Math.min(prev + 1, totalSteps));
  };

  const prevStep = () => {
    setDirection(-1);
    setStep(prev => Math.max(prev - 1, 0));
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/prospect`, formData);
      setIsSuccess(true);
      triggerConfetti();
      toast.success("Votre demande a été envoyée avec succès !");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStep1Valid = formData.name && formData.phone && formData.email;
  const isStep2Valid = formData.is_land_owner !== null;
  const isStep3Valid = formData.permit_status !== null;
  const isStep4Valid = formData.has_consulted_bank !== null;
  const isStep5Valid = formData.desired_timeline !== null;

  const renderStepIndicator = () => (
    <div className="step-indicator">
      {[...Array(totalSteps)].map((_, i) => (
        <div 
          key={i} 
          className={`step-dot ${i === step ? 'active' : ''} ${i < step ? 'completed' : ''}`}
        />
      ))}
    </div>
  );

  const renderProgress = () => (
    <div className="progress-bar mb-6">
      <div 
        className="progress-fill" 
        style={{ width: `${(step / totalSteps) * 100}%` }}
      />
    </div>
  );

  // Step 0: Intro
  const renderIntro = () => (
    <motion.div
      key="intro"
      custom={direction}
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{ duration: 0.3 }}
      className="text-center"
    >
      <div className="logo-container">
        <img src={LOGO_URL} alt="Belgian Lodges" data-testid="logo" />
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A2F23] mb-4">
        Votre Projet de Lodge en Bois
      </h1>
      <p className="text-[#5C5C5C] text-lg mb-8 max-w-md mx-auto">
        Répondez à quelques questions pour nous permettre de mieux comprendre votre projet et vous accompagner au mieux.
      </p>
      <button 
        onClick={nextStep}
        className="btn-gold"
        data-testid="start-button"
      >
        Commencer
        <ChevronRight size={20} />
      </button>
    </motion.div>
  );

  // Step 1: Contact Info
  const renderContactInfo = () => (
    <motion.div
      key="contact"
      custom={direction}
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{ duration: 0.3 }}
    >
      <h2 className="font-heading text-2xl font-semibold text-[#1A2F23] mb-6 text-center">
        Vos Coordonnées
      </h2>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[#5C5C5C] mb-2">
            <User size={16} className="inline mr-2" />
            Nom complet
          </label>
          <input
            type="text"
            className="input-field"
            placeholder="Jean Dupont"
            value={formData.name}
            onChange={(e) => updateFormData('name', e.target.value)}
            data-testid="input-name"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-[#5C5C5C] mb-2">
            <Phone size={16} className="inline mr-2" />
            Numéro de téléphone
          </label>
          <input
            type="tel"
            className="input-field"
            placeholder="+32 XXX XX XX XX"
            value={formData.phone}
            onChange={(e) => updateFormData('phone', e.target.value)}
            data-testid="input-phone"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-[#5C5C5C] mb-2">
            <Mail size={16} className="inline mr-2" />
            Adresse email
          </label>
          <input
            type="email"
            className="input-field"
            placeholder="jean.dupont@email.be"
            value={formData.email}
            onChange={(e) => updateFormData('email', e.target.value)}
            data-testid="input-email"
          />
        </div>
      </div>

      <div className="flex justify-between mt-8">
        <button onClick={prevStep} className="btn-outline" data-testid="btn-back">
          <ChevronLeft size={18} className="inline mr-1" />
          Retour
        </button>
        <button 
          onClick={nextStep} 
          className="btn-gold"
          disabled={!isStep1Valid}
          data-testid="btn-next-1"
        >
          Suivant
          <ChevronRight size={20} />
        </button>
      </div>
    </motion.div>
  );

  // Step 2: Land Ownership
  const renderLandOwnership = () => (
    <motion.div
      key="land"
      custom={direction}
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{ duration: 0.3 }}
    >
      <h2 className="font-heading text-2xl font-semibold text-[#1A2F23] mb-2 text-center">
        Êtes-vous propriétaire de votre terrain ?
      </h2>
      <p className="text-[#5C5C5C] text-center mb-6">
        Cela nous aide à comprendre l'avancement de votre projet.
      </p>
      
      <div className="space-y-3">
        <div 
          className={`option-card ${formData.is_land_owner === true ? 'selected' : ''}`}
          onClick={() => updateFormData('is_land_owner', true)}
          data-testid="option-land-yes"
        >
          <div className="icon-wrapper">
            <Home size={24} />
          </div>
          <div>
            <div className="font-medium text-[#1A2F23]">Oui, je suis propriétaire</div>
            <div className="text-sm text-[#5C5C5C]">J'ai un terrain prêt pour la construction</div>
          </div>
          {formData.is_land_owner === true && <Check className="ml-auto text-[#D4AF37]" size={24} />}
        </div>
        
        <div 
          className={`option-card ${formData.is_land_owner === false ? 'selected' : ''}`}
          onClick={() => updateFormData('is_land_owner', false)}
          data-testid="option-land-no"
        >
          <div className="icon-wrapper">
            <FileText size={24} />
          </div>
          <div>
            <div className="font-medium text-[#1A2F23]">Non, pas encore</div>
            <div className="text-sm text-[#5C5C5C]">Je suis en recherche de terrain</div>
          </div>
          {formData.is_land_owner === false && <Check className="ml-auto text-[#D4AF37]" size={24} />}
        </div>
      </div>

      <div className="flex justify-between mt-8">
        <button onClick={prevStep} className="btn-outline" data-testid="btn-back-2">
          <ChevronLeft size={18} className="inline mr-1" />
          Retour
        </button>
        <button 
          onClick={nextStep} 
          className="btn-gold"
          disabled={!isStep2Valid}
          data-testid="btn-next-2"
        >
          Suivant
          <ChevronRight size={20} />
        </button>
      </div>
    </motion.div>
  );

  // Step 3: Permit Status
  const renderPermitStatus = () => (
    <motion.div
      key="permit"
      custom={direction}
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{ duration: 0.3 }}
    >
      <h2 className="font-heading text-2xl font-semibold text-[#1A2F23] mb-2 text-center">
        Avez-vous consulté votre commune ?
      </h2>
      <p className="text-[#5C5C5C] text-center mb-6">
        Concernant la possibilité d'obtenir un permis de construire.
      </p>
      
      <div className="space-y-3">
        <div 
          className={`option-card ${formData.permit_status === 'yes' ? 'selected' : ''}`}
          onClick={() => updateFormData('permit_status', 'yes')}
          data-testid="option-permit-yes"
        >
          <div className="icon-wrapper">
            <Check size={24} />
          </div>
          <div>
            <div className="font-medium text-[#1A2F23]">Oui, c'est fait</div>
            <div className="text-sm text-[#5C5C5C]">J'ai déjà les informations nécessaires</div>
          </div>
          {formData.permit_status === 'yes' && <Check className="ml-auto text-[#D4AF37]" size={24} />}
        </div>
        
        <div 
          className={`option-card ${formData.permit_status === 'in_progress' ? 'selected' : ''}`}
          onClick={() => updateFormData('permit_status', 'in_progress')}
          data-testid="option-permit-progress"
        >
          <div className="icon-wrapper">
            <Clock size={24} />
          </div>
          <div>
            <div className="font-medium text-[#1A2F23]">En cours</div>
            <div className="text-sm text-[#5C5C5C]">Les démarches sont en cours</div>
          </div>
          {formData.permit_status === 'in_progress' && <Check className="ml-auto text-[#D4AF37]" size={24} />}
        </div>
        
        <div 
          className={`option-card ${formData.permit_status === 'no' ? 'selected' : ''}`}
          onClick={() => updateFormData('permit_status', 'no')}
          data-testid="option-permit-no"
        >
          <div className="icon-wrapper">
            <FileText size={24} />
          </div>
          <div>
            <div className="font-medium text-[#1A2F23]">Non, pas encore</div>
            <div className="text-sm text-[#5C5C5C]">Je n'ai pas encore fait cette démarche</div>
          </div>
          {formData.permit_status === 'no' && <Check className="ml-auto text-[#D4AF37]" size={24} />}
        </div>
      </div>

      <div className="flex justify-between mt-8">
        <button onClick={prevStep} className="btn-outline" data-testid="btn-back-3">
          <ChevronLeft size={18} className="inline mr-1" />
          Retour
        </button>
        <button 
          onClick={nextStep} 
          className="btn-gold"
          disabled={!isStep3Valid}
          data-testid="btn-next-3"
        >
          Suivant
          <ChevronRight size={20} />
        </button>
      </div>
    </motion.div>
  );

  // Step 4: Bank Financing
  const renderBankFinancing = () => (
    <motion.div
      key="bank"
      custom={direction}
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{ duration: 0.3 }}
    >
      <h2 className="font-heading text-2xl font-semibold text-[#1A2F23] mb-2 text-center">
        Avez-vous consulté votre banque ?
      </h2>
      <p className="text-[#5C5C5C] text-center mb-6">
        Pour connaître votre capacité de financement.
      </p>
      
      <div className="space-y-3">
        <div 
          className={`option-card ${formData.has_consulted_bank === true ? 'selected' : ''}`}
          onClick={() => updateFormData('has_consulted_bank', true)}
          data-testid="option-bank-yes"
        >
          <div className="icon-wrapper">
            <Banknote size={24} />
          </div>
          <div>
            <div className="font-medium text-[#1A2F23]">Oui, c'est fait</div>
            <div className="text-sm text-[#5C5C5C]">Je connais ma capacité de financement</div>
          </div>
          {formData.has_consulted_bank === true && <Check className="ml-auto text-[#D4AF37]" size={24} />}
        </div>
        
        <div 
          className={`option-card ${formData.has_consulted_bank === false ? 'selected' : ''}`}
          onClick={() => updateFormData('has_consulted_bank', false)}
          data-testid="option-bank-no"
        >
          <div className="icon-wrapper">
            <FileText size={24} />
          </div>
          <div>
            <div className="font-medium text-[#1A2F23]">Non, pas encore</div>
            <div className="text-sm text-[#5C5C5C]">Je n'ai pas encore consulté ma banque</div>
          </div>
          {formData.has_consulted_bank === false && <Check className="ml-auto text-[#D4AF37]" size={24} />}
        </div>
      </div>

      <div className="flex justify-between mt-8">
        <button onClick={prevStep} className="btn-outline" data-testid="btn-back-4">
          <ChevronLeft size={18} className="inline mr-1" />
          Retour
        </button>
        <button 
          onClick={nextStep} 
          className="btn-gold"
          disabled={!isStep4Valid}
          data-testid="btn-next-4"
        >
          Suivant
          <ChevronRight size={20} />
        </button>
      </div>
    </motion.div>
  );

  // Step 5: Timeline
  const renderTimeline = () => (
    <motion.div
      key="timeline"
      custom={direction}
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{ duration: 0.3 }}
    >
      <h2 className="font-heading text-2xl font-semibold text-[#1A2F23] mb-2 text-center">
        Quel est votre délai souhaité ?
      </h2>
      <p className="text-[#5C5C5C] text-center mb-6">
        Pour la réalisation de votre projet de lodge.
      </p>
      
      <div className="space-y-3">
        {[
          { value: 'less_than_6_months', label: 'Moins de 6 mois', desc: 'Projet urgent' },
          { value: '6_to_12_months', label: '6 à 12 mois', desc: 'Délai standard' },
          { value: '1_to_2_years', label: '1 à 2 ans', desc: 'Projet à moyen terme' },
          { value: 'more_than_2_years', label: 'Plus de 2 ans', desc: 'Projet à long terme' },
          { value: 'not_sure', label: 'Je ne sais pas encore', desc: 'À définir ensemble' }
        ].map((option) => (
          <div 
            key={option.value}
            className={`option-card ${formData.desired_timeline === option.value ? 'selected' : ''}`}
            onClick={() => updateFormData('desired_timeline', option.value)}
            data-testid={`option-timeline-${option.value}`}
          >
            <div className="icon-wrapper">
              <Calendar size={24} />
            </div>
            <div>
              <div className="font-medium text-[#1A2F23]">{option.label}</div>
              <div className="text-sm text-[#5C5C5C]">{option.desc}</div>
            </div>
            {formData.desired_timeline === option.value && <Check className="ml-auto text-[#D4AF37]" size={24} />}
          </div>
        ))}
      </div>

      <div className="flex justify-between mt-8">
        <button onClick={prevStep} className="btn-outline" data-testid="btn-back-5">
          <ChevronLeft size={18} className="inline mr-1" />
          Retour
        </button>
        <button 
          onClick={handleSubmit} 
          className="btn-gold"
          disabled={!isStep5Valid || isSubmitting}
          data-testid="btn-submit"
        >
          {isSubmitting ? (
            <>
              <Loader2 size={20} className="animate-spin" />
              Envoi...
            </>
          ) : (
            <>
              Envoyer
              <Check size={20} />
            </>
          )}
        </button>
      </div>
    </motion.div>
  );

  // Success Screen
  const renderSuccess = () => (
    <motion.div
      key="success"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      className="text-center py-8"
    >
      <div className="logo-container">
        <img src={LOGO_URL} alt="Belgian Lodges" />
      </div>
      
      <div className="success-icon inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#2E8B57] text-white mb-6">
        <Check size={40} />
      </div>
      
      <h2 className="font-heading text-3xl font-bold text-[#1A2F23] mb-4">
        Merci {formData.name} !
      </h2>
      
      <p className="text-[#5C5C5C] text-lg mb-6 max-w-md mx-auto">
        Vos informations ont été envoyées avec succès. Notre équipe vous contactera très prochainement pour discuter de votre projet de lodge en bois.
      </p>
      
      <a 
        href="https://www.construction-lodge-en-bois.be"
        className="btn-gold inline-flex"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="btn-visit-site"
      >
        Visiter notre site
        <ChevronRight size={20} />
      </a>
    </motion.div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="questionnaire-bg">
        <video autoPlay muted loop playsInline>
          <source src={VIDEO_BG_URL} type="video/mp4" />
        </video>
      </div>
      
      <div className="glass-card w-full max-w-xl p-8 sm:p-10">
        {!isSuccess && step > 0 && renderProgress()}
        {!isSuccess && step > 0 && renderStepIndicator()}
        
        <AnimatePresence mode="wait" custom={direction}>
          {isSuccess ? renderSuccess() : (
            <>
              {step === 0 && renderIntro()}
              {step === 1 && renderContactInfo()}
              {step === 2 && renderLandOwnership()}
              {step === 3 && renderPermitStatus()}
              {step === 4 && renderBankFinancing()}
              {step === 5 && renderTimeline()}
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<QuestionnaireForm />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
